// Pengguna (user management): the page, its filters, the drawer, the forms and every confirmation.
import { ROLE_LABEL } from './labels';

export const USERS = {
  title: 'Pengguna',
  sub: 'Setiap orang punya akun sendiri, sehingga setiap perubahan tercatat atas nama yang melakukannya.',
  add: 'Tambah pengguna',
  loading: 'Memuat pengguna…',
  noun: 'pengguna',
  you: 'Anda',
  active: 'Aktif',
  inactive: 'Nonaktif',
  never: 'Belum pernah masuk',
  /** the list's badge for an account with two-step verification on */
  totpBadge: '2 langkah',
  totpOn: 'Verifikasi dua langkah aktif',
  noLocation: 'Belum ada lokasi',
  columns: { name: 'Nama', role: 'Peran', scope: 'Cakupan', status: 'Status', lastLogin: 'Terakhir masuk' },
  empty: { title: 'Belum ada pengguna', text: 'Buat akun untuk setiap orang yang memakai aplikasi ini.' },
  emptyScoped: (where: string) => ({ title: 'Belum ada pengguna lain', text: `Buat akun untuk petugas, pemantau, dan pengawas di ${where}.` }),
  noMatch: { title: 'Tidak ada pengguna yang cocok', text: 'Ubah kata kunci, peran, cakupan, atau status.' },
} as const;

/** Toolbar filters (menus on laptops, a sheet on phones). */
export const USER_FILTERS = {
  search: 'Cari pengguna',
  searchPlaceholder: 'Cari nama atau email…',
  searchPlaceholderShort: 'Cari pengguna…',
  role: 'Peran',
  allRoles: 'Semua peran',
  scope: 'Cakupan',
  allScopes: 'Semua cakupan',
  scopeNational: 'Nasional',
  scopeProvince: 'Provinsi',
  scopeProvinceSub: 'Admin, Pemantau, Pengawas satu provinsi',
  scopeLocation: 'Titik Distribusi',
  scopeLocationSub: `${ROLE_LABEL.operator} satu Titik Distribusi`,
  inProvince: 'Di provinsi',
  anyProvince: 'Semua provinsi',
  provinceSearch: 'Cari provinsi…',
  noProvince: 'Tidak ada provinsi yang cocok.',
  status: 'Status',
  allStatus: 'Semua status',
} as const;

/** The ⋯ menu of a row and the drawer's actions. */
export const USER_ACTIONS = {
  menu: (name: string) => `Tindakan untuk ${name}`,
  view: 'Lihat rincian',
  access: 'Ubah peran & cakupan',
  reset: 'Buat kata sandi baru',
  reinvite: 'Kirim ulang undangan',
  resetTotp: 'Atur ulang verifikasi dua langkah',
  signOutAll: 'Keluarkan dari semua perangkat',
  deactivate: 'Nonaktifkan',
  activate: 'Aktifkan',
  pickLocation: 'Pilih lokasi',
  remove: 'Hapus akun',
} as const;

export const USER_DRAWER = {
  label: (name: string) => `Rincian pengguna ${name}`,
  access: 'Akses',
  account: 'Akun',
  role: 'Peran',
  scope: 'Cakupan',
  change: 'Ubah',
  status: 'Status',
  lastLogin: 'Terakhir masuk',
  created: 'Akun dibuat',
  createdBy: (name: string) => `oleh ${name}`,
  sessions: 'Perangkat yang sedang masuk',
  sessionsNone: 'Tidak sedang masuk di perangkat mana pun.',
  sessionsFailed: 'Daftar perangkat tidak dapat dimuat.',
  activity: 'Aktivitas terakhir',
  activityNone: 'Belum ada aktivitas tercatat.',
  activityAll: 'Lihat semua di Log aktivitas',
  activityAllHere: 'Lihat semua',
  sessionsNoun: 'perangkat',
  activityNoun: 'aktivitas',
  detailFailed: 'Rincian pengguna tidak dapat dimuat.',
  self: 'Ini akun Anda. Ubah nama, kata sandi, dan verifikasi dua langkah di menu Akun; peran dan status akun Anda diubah oleh admin lain.',
  totp: 'Verifikasi dua langkah',
  totpOn: 'Aktif',
  totpOff: 'Belum aktif',
  totpRequired: 'Wajib untuk admin nasional',
  inactiveNote: 'Akun ini nonaktif: tidak bisa masuk sampai diaktifkan lagi.',
  noLocationNote: 'Lokasi tugas akun ini sudah tidak ada. Pilih lokasi baru dulu, lalu aktifkan.',
  outOfReach: 'Akun ini di luar wewenang Anda: hanya admin nasional yang bisa mengubahnya.',
} as const;

/** Role cards and the scope step of the add and access forms. */
export const ACCESS_FORM = {
  role: 'Peran',
  scope: 'Cakupan wilayah',
  national: 'Nasional',
  nationalHelp: 'Seluruh Indonesia.',
  province: 'Provinsi',
  provinceHelp: 'Hanya data lokasi di satu provinsi.',
  provincePick: 'Provinsi',
  provincePlaceholder: 'Cari provinsi…',
  provinceRequired: 'Pilih provinsi.',
  provinceFixed: (name: string) => `Akun yang Anda buat berlaku di ${name}, wilayah akun Anda.`,
  location: 'Lokasi tugas',
  locationHelp: 'Satu akun untuk satu lokasi: setelah masuk, petugas langsung bekerja di lokasi ini.',
  locationRequired: `Pilih lokasi tugas. Setiap akun ${ROLE_LABEL.operator} bertugas di satu lokasi.`,
  noProvince: 'Tidak ada provinsi yang cocok.',
} as const;

export const USER_FORM = {
  title: 'Tambah pengguna',
  name: 'Nama',
  email: 'Email',
  nameRequired: 'Nama wajib diisi.',
  emailInvalid: 'Isi alamat email yang valid.',
  passwordNote: 'Kata sandi sementara dibuat otomatis dan ditampilkan sekali setelah akun dibuat.',
  submit: 'Buat akun',
  created: (email: string) => `Akun ${email} dibuat`,
  noPassword: (name: string) => `Sistem tidak mengirim kata sandi sementara. Buka ${name} lalu pilih Buat kata sandi baru.`,
  provinceLabel: 'Provinsi',
  locationLabel: 'Lokasi tugas',
  roleLabel: 'Peran',
} as const;

/** Changing someone's role and scope: the dialog and its confirmation. */
export const ACCESS_CHANGE = {
  title: (name: string) => `Peran & cakupan ${name}`,
  save: 'Lanjutkan',
  unchanged: 'Belum ada yang diubah.',
  confirmTitle: (name: string) => `Ubah akses ${name}?`,
  confirm: 'Simpan perubahan',
  roleLine: (from: string, to: string) => `Peran: ${from} → ${to}`,
  scopeLine: (from: string, to: string) => `Cakupan: ${from} → ${to}`,
  reachNational: 'Akses ke seluruh Indonesia.',
  reachProvince: (name: string) => `Akses ke ${name} saja.`,
  reachLocation: (name: string) => `Bekerja di ${name} saja.`,
  adminWarning: 'Admin bisa mengubah lokasi, gateway, tabung, dan akun pengguna lain di wilayahnya.',
  signOutNote: 'Perubahan langsung berlaku di setiap perangkat yang sedang masuk.',
  done: (name: string) => `Akses ${name} diubah`,
} as const;

export const USER_CONFIRM = {
  reinviteTitle: (name: string) => `Kirim ulang undangan aktivasi ke ${name}?`,
  reinviteBody: 'Undangan baru dikirim ke email penanggung jawab Hub. Undangan sebelumnya tidak berlaku lagi.',
  reinviteDone: 'Undangan dikirim',
  reinviteNotAllowed: (name: string) => `Undangan ${name} sudah digunakan atau akun ini bukan akun Petugas Hub, sehingga undangan baru tidak dapat dikirim.`,
  invite: {
    sent: (to: string, until: string) => `Undangan aktivasi telah dikirim ke ${to}. Berlaku sampai ${until}.`,
    manual: (until: string) => `Email otomatis belum aktif. Sampaikan tautan aktivasi berikut secara langsung kepada penanggung jawab, jangan melalui grup. Tautan berlaku sampai ${until} dan hanya dapat digunakan sekali.`,
    link: 'Tautan aktivasi',
    copy: 'Salin tautan',
    copied: 'Tautan disalin',
    copyFailed: 'Tautan tidak dapat disalin otomatis. Pilih dan salin secara manual.',
  },
  resetTitle: (name: string) => `Buat kata sandi baru untuk ${name}?`,
  resetBody: 'Kata sandi lama langsung tidak berlaku dan semua perangkat ikut keluar. Kata sandi baru ditampilkan sekali.',
  resetLabel: 'Buat kata sandi baru',
  resetUnsureTitle: 'Kata sandi baru belum pasti tersimpan',
  resetUnsureBody: (name: string) => `Sistem tidak menjawab, sehingga kata sandi lama ${name} mungkin sudah tidak berlaku. Kirim ulang kata sandi baru yang sama agar pasti berlaku dan dapat Anda lihat.`,
  resetUnsureLabel: 'Kirim ulang',
  resetGaveUp: (name: string) => `Kata sandi ${name} belum pasti berubah. Buat kata sandi baru lagi bila ${name} tidak dapat masuk.`,
  totpResetTitle: (name: string) => `Atur ulang verifikasi dua langkah ${name}?`,
  totpResetBodyRequired: (name: string) => `${name} langsung keluar dari semua perangkat dan wajib mendaftarkan aplikasi autentikator lagi saat masuk berikutnya. Kode pemulihan lamanya tidak berlaku lagi.`,
  totpResetBodyOptional: (name: string) => `${name} langsung keluar dari semua perangkat dan masuk berikutnya cukup dengan kata sandi, lalu dapat mengaktifkan verifikasi dua langkah lagi di menu Akun. Kode pemulihan lamanya tidak berlaku lagi.`,
  totpResetLabel: 'Atur ulang',
  totpReset: (name: string) => `Verifikasi dua langkah ${name} diatur ulang`,
  signOutTitle: (name: string) => `Keluarkan ${name} dari semua perangkat?`,
  signOutBody: (n: number) => `${n > 0 ? `${n} perangkat` : 'Setiap perangkat'} harus masuk lagi dengan kata sandi. Akun tetap aktif.`,
  signOutLabel: 'Keluarkan semua',
  signedOut: (name: string, n: number) => (n ? `${name} dikeluarkan dari ${n} perangkat` : `${name} tidak sedang masuk di perangkat mana pun`),
  deactivateTitle: (name: string) => `Nonaktifkan ${name}?`,
  deactivateBody: 'Pengguna langsung keluar dari semua perangkat dan tidak bisa masuk lagi sampai diaktifkan kembali. Riwayatnya tetap tersimpan.',
  deactivated: (name: string) => `${name} dinonaktifkan`,
  activated: (name: string) => `${name} diaktifkan`,
  removeTitle: (name: string) => `Hapus akun ${name}?`,
  removeBody: 'Akun ini belum pernah dipakai, jadi bisa dihapus permanen. Akun yang sudah pernah dipakai cukup dinonaktifkan agar riwayatnya tetap utuh.',
  removeLabel: 'Hapus akun',
  removed: (name: string) => `Akun ${name} dihapus`,
  inUse: 'Akun ini sudah pernah dipakai, jadi tidak bisa dihapus. Nonaktifkan saja: riwayatnya tetap tercatat.',
} as const;

export const SECRET = {
  title: 'Kata sandi sementara',
  body: (email: string) => `Berikan kata sandi ini kepada ${email} melalui jalur pribadi, misalnya pesan langsung. Minta pengguna menggantinya di menu Akun setelah masuk.`,
  once: 'Kata sandi ini hanya ditampilkan sekali. Setelah jendela ini ditutup, kata sandi tidak dapat dilihat lagi.',
  copy: 'Salin',
  copied: 'Kata sandi disalin',
  copyFailed: 'Tidak dapat menyalin otomatis. Pilih teks kata sandi lalu salin secara manual.',
  done: 'Sudah saya catat',
} as const;
